import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AutoReplyBucketDTO, ChannelAutoReplyDTO, ChannelType } from '@sac/contracts'
import { api } from '../../lib/api'
import { qk } from '../../lib/query'
import { ChannelMark, channelName } from '../icons'
import { useToast } from '../ui/Toaster'

/**
 * Configurações → Resposta automática.
 *
 * A tela existe para uma decisão só, e ela não é reversível depois de acontecer: com isto ligado
 * a loja responde clientes sem ninguém ler. Numa pergunta pública do Mercado Livre a resposta
 * ainda é única e definitiva — não há como editar depois.
 *
 * Por isso a calibração fica ao lado do controle, e não escondida atrás de um link. A nota de
 * certeza é estrutural: mede COMO a resposta foi produzida, não se ela está certa. A única
 * evidência de quanto ela vale é como as sugestões daquele canal se saíram nas mãos de gente,
 * e é isso que a tabela mostra.
 */

const CANAL_LABEL: Partial<Record<ChannelType, string>> = {
  mercadolivre: 'Mercado Livre (pós-venda)',
  mercadolivre_question: 'Mercado Livre (perguntas)',
  tiktok: 'TikTok (atendimento)',
  tiktok_comment: 'TikTok (comentários)',
}

function pct(parte: number, total: number) {
  return total ? Math.round((100 * parte) / total) : 0
}

/**
 * Como as sugestões daquele canal se saíram, por faixa de nota.
 *
 * "Enviada sem editar" é o mais perto que temos de "estava certa" — é o caso em que alguém leu e
 * não mudou nada. Não é a mesma coisa: uma edição pode ter sido de tom. Mas é o único sinal
 * medido que existe, e mostrar ele cru é melhor que oferecer um número inventado.
 */
function Calibracao({ buckets, minimo }: { buckets: AutoReplyBucketDTO[]; minimo: number }) {
  if (!buckets.length) {
    return (
      <p className="cfg-note">
        Nenhuma sugestão deste canal foi avaliada por uma pessoa ainda. Sem isso não há como saber
        o que uma nota mínima significa aqui — vale usar o painel de sugestões um tempo antes de
        ligar.
      </p>
    )
  }
  const faixaLo = (b: string) => Number(b.split('-')[0])
  return (
    <table className="ar-cal">
      <thead>
        <tr>
          <th>Nota</th>
          <th>Avaliadas</th>
          <th>Enviadas sem editar</th>
          <th>Editadas</th>
          <th>Descartadas</th>
        </tr>
      </thead>
      <tbody>
        {buckets.map((b) => (
          // A faixa que o mínimo atual deixa passar fica marcada: é a linha que descreve o que
          // vai acontecer, e não a melhor linha da tabela.
          <tr key={b.band} className={faixaLo(b.band) >= minimo ? 'dentro' : ''}>
            <td>{b.band}</td>
            <td>{b.n}</td>
            <td>
              <b>{pct(b.verbatim, b.n)}%</b> <span className="t2">({b.verbatim})</span>
            </td>
            <td className="t2">{pct(b.edited, b.n)}% ({b.edited})</td>
            <td className="t2">{pct(b.discarded, b.n)}% ({b.discarded})</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function Canal({ c }: { c: ChannelAutoReplyDTO }) {
  const qc = useQueryClient()
  const toast = useToast()
  const [minimo, setMinimo] = useState(c.minCertainty)
  const [aberto, setAberto] = useState(false)

  const salvar = useMutation({
    mutationFn: (v: { enabled: boolean; minCertainty: number }) => api.setAutoReply(c.channelType, v),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: qk.autoReply() })
      toast(
        v.enabled
          ? `Resposta automática LIGADA em ${CANAL_LABEL[c.channelType] ?? channelName(c.channelType)} a partir de ${v.minCertainty}.`
          : `Resposta automática desligada em ${CANAL_LABEL[c.channelType] ?? channelName(c.channelType)}.`,
      )
    },
    onError: (e: Error) => toast(e.message),
  })

  const ligar = () => {
    if (!c.enabled) {
      // Uma confirmação explícita, porque o efeito é um clique agora e uma mensagem a um cliente
      // real daqui a minutos, sem mais nenhuma tela no meio.
      const nome = CANAL_LABEL[c.channelType] ?? channelName(c.channelType)
      if (!confirm(`Ligar resposta automática em ${nome}?\n\nA partir daí a IA responde clientes deste canal sozinha, sem ninguém ler, sempre que a nota for ${minimo} ou mais.`)) return
    }
    salvar.mutate({ enabled: !c.enabled, minCertainty: minimo })
  }

  return (
    <div className={`ar-row ${c.enabled ? 'on' : ''}`}>
      <div className="ar-head">
        <ChannelMark channel={c.channelType} size={16} />
        <b>{CANAL_LABEL[c.channelType] ?? channelName(c.channelType)}</b>
        <span className="t2">{c.conversations} conversa(s)</span>
        {c.enabled && <span className="ar-on">ligada a partir de {c.minCertainty}</span>}
        <span className="ar-sp" />
        {c.updatedAt && (
          <span className="t2">
            alterado por {c.updatedByName ?? '—'} em {new Date(c.updatedAt).toLocaleDateString('pt-BR')}
          </span>
        )}
        <button className="btn sec sm" onClick={() => setAberto((v) => !v)}>
          {aberto ? 'Fechar' : 'Ajustar'}
        </button>
        <button className={`btn sm ${c.enabled ? 'sec' : ''}`} onClick={ligar} disabled={salvar.isPending}>
          {c.enabled ? 'Desligar' : 'Ligar'}
        </button>
      </div>

      {aberto && (
        <div className="ar-body">
          <label className="ar-min">
            Nota mínima para responder sozinho
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={minimo}
              onChange={(e) => setMinimo(Number(e.target.value))}
            />
            <b>{minimo}</b>
          </label>
          {minimo !== c.minCertainty && (
            <button className="btn sm" onClick={() => salvar.mutate({ enabled: c.enabled, minCertainty: minimo })}>
              Salvar mínimo
            </button>
          )}
          <Calibracao buckets={c.calibration} minimo={minimo} />
        </div>
      )}
    </div>
  )
}

export function AutoReplyTab() {
  const { data, isLoading } = useQuery({ queryKey: qk.autoReply(), queryFn: api.autoReply })

  if (isLoading) return <div className="empty">Carregando…</div>
  if (!data) return <div className="empty">Sem dados.</div>

  // Canais sem nenhuma conversa não aparecem: ligar resposta automática num canal que não recebe
  // nada é uma configuração que não faz nada e uma linha a mais para conferir.
  const canais = data.channels.filter((c) => c.conversations > 0)

  return (
    <div className="cfg-sec">
      <h3>Resposta automática</h3>
      <p className="cfg-note">
        Com isto ligado, a IA responde clientes deste canal <b>sem ninguém ler antes</b>. A nota
        mínima é o botão, não a trava: uma resposta bloqueada pelo compliance, com número ou
        afirmação sem fonte, ou que não seja uma resposta (uma pergunta de volta ao cliente, um
        pedido de ajuda ao time) nunca sai sozinha, com nota nenhuma.
      </p>
      <p className="cfg-note">
        A nota mede <b>como</b> a resposta foi produzida — o que a fundamentou, se algum número
        tem fonte — e não se ela está certa. Abra "Ajustar" para ver como as sugestões daquele
        canal se saíram de fato antes de escolher o mínimo.
      </p>

      {!data.globallyEnabled && (
        <p className="cfg-note danger">
          <b>AUTO_REPLY_ENABLED=false no ambiente.</b> Nada é enviado automaticamente enquanto isso
          estiver assim, independente do que estiver ligado aqui.
        </p>
      )}

      <div className="ar-list">
        {canais.map((c) => (
          <Canal key={c.channelType} c={c} />
        ))}
      </div>
    </div>
  )
}
